import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  sendEmailVerification,
  onAuthStateChanged,
  signOut,
  deleteUser,
} from "firebase/auth";
import { auth, db } from "../FirebaseConfig";
import { toast } from "react-toastify";
import { doc, setDoc } from "firebase/firestore";
import { Eye, EyeOff, Mail, CheckCircle } from "lucide-react";

const RegisterPage = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [contact, setContact] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [verificationStep, setVerificationStep] = useState("form"); // 'form', 'verification', 'verified'
  const [verificationEmail, setVerificationEmail] = useState("");
  const [tempUser, setTempUser] = useState(null);
  const [checkingInterval, setCheckingInterval] = useState(null);
  const navigate = useNavigate();
  const [resendCount, setResendCount] = useState(0);
  const [lastResendTime, setLastResendTime] = useState(null);
  const [isResendCooldown, setIsResendCooldown] = useState(false);
  const maxResendAttempts = 1; // Maximum resend attempts
  const cooldownPeriod = 60 * 1000; // 60 seconds in milliseconds

  const resendVerificationEmail = async () => {
    try {
      // Check if maximum resend attempts reached
      if (resendCount >= maxResendAttempts) {
        toast.error(`Maximum resend attempts (${maxResendAttempts}) reached. Please try again later or contact support.`);
        return;
      }
  
      // Check if cooldown period is active
      if (lastResendTime && Date.now() - lastResendTime < cooldownPeriod) {
        toast.error(`Please wait ${Math.ceil((cooldownPeriod - (Date.now() - lastResendTime)) / 1000)} seconds before resending again.`);
        return;
      }
  
      // Ensure tempUser exists and is not stale
      if (!tempUser) {
        toast.error("No user session found. Please restart the registration process.");
        setVerificationStep("form");
        return;
      }
  
      // Ensure user is still authenticated
      if (!auth.currentUser || auth.currentUser.uid !== tempUser.uid) {
        toast.error("Authentication session expired. Please sign in again.");
        await signOut(auth);
        localStorage.removeItem("tempRegistrationData");
        setVerificationStep("form");
        setTempUser(null);
        return;
      }
  
      // Attempt to resend verification email
      await sendEmailVerification(tempUser, {
        url: `${window.location.origin}/profile`,
        handleCodeInApp: true,
      });
  
      setResendCount((prev) => prev + 1);
      setLastResendTime(Date.now());
      setIsResendCooldown(true);
  
      // Reset cooldown after the period
      setTimeout(() => {
        setIsResendCooldown(false);
      }, cooldownPeriod);
  
      toast.success(`Verification email resent! (${resendCount + 1}/${maxResendAttempts})`);
    } catch (error) {
      console.error("Resend email error:", error);
      let errorMessage = "Failed to resend verification email. Please try again.";
      
      // Handle specific Firebase errors
      switch (error.code) {
        case "auth/too-many-requests":
          errorMessage = "Too many requests. Please wait a few minutes and try again.";
          break;
        case "auth/user-not-found":
          errorMessage = "User session not found. Please restart registration.";
          setVerificationStep("form");
          setTempUser(null);
          localStorage.removeItem("tempRegistrationData");
          break;
        case "auth/network-request-failed":
          errorMessage = "Network error. Please check your internet connection.";
          break;
        case "auth/operation-not-allowed":
          errorMessage = "Email verification is disabled. Please contact support.";
          break;
        default:
          errorMessage = `Error: ${error.message}`;
      }
  
      toast.error(errorMessage);
    }
  };
  // Comprehensive email validation
  const validateEmail = (email) => {
    const emailRegex =
      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    return emailRegex.test(email.toLowerCase());
  };

  // Phone number validation
  const validatePhoneNumber = (contact) => {
    const phoneRegex = /^\+?([0-9]{1,4})?[-.\s]?([0-9]{6,14})$/;
    return phoneRegex.test(contact);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!validateEmail(email)) {
      toast.error(
        "Please enter a valid email address (e.g., user@example.com)."
      );
      setIsSubmitting(false);
      return;
    }

    if (!validatePhoneNumber(contact)) {
      toast.error("Please enter a valid contact number (e.g., +1234567890).");
      setIsSubmitting(false);
      return;
    }

    try {
      // Create temporary Firebase account (will be deleted if not verified)
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;

      // Store user temporarily (not in Firestore yet)
      setTempUser(user);

      // Store registration data temporarily
      const tempUserData = {
        name,
        email: email.toLowerCase(),
        contact,
        uid: user.uid,
      };
      localStorage.setItem(
        "tempRegistrationData",
        JSON.stringify(tempUserData)
      );

      // Send email verification
      await sendEmailVerification(user, {
        url: `${window.location.origin}/profile`,
        handleCodeInApp: true,
      });

      setVerificationEmail(email);
      setVerificationStep("verification");

      // Start automatic verification checking
      startVerificationCheck();

      toast.success(
        "Verification email sent! Please check your inbox and click the verification link."
      );
    } catch (error) {
      console.error("Registration error:", error);
      let errorMessage = "Registration failed. Please try again.";

      switch (error.code) {
        case "auth/email-already-in-use":
          errorMessage = "An account with this email already exists";
          break;
        case "auth/invalid-email":
          errorMessage = "Please enter a valid email address";
          break;
        case "auth/weak-password":
          errorMessage =
            "Password is too weak. Please choose a stronger password";
          break;
        case "auth/network-request-failed":
          errorMessage = "Network error. Please check your connection";
          break;
        case "auth/operation-not-allowed":
          errorMessage =
            "Email/password authentication is not enabled. Please contact support.";
          break;
        default:
          errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const startVerificationCheck = () => {
    // Check every 3 seconds for email verification
    const interval = setInterval(async () => {
      if (auth.currentUser) {
        await auth.currentUser.reload();
        if (auth.currentUser.emailVerified) {
          clearInterval(interval);
          setCheckingInterval(null);
          await completeRegistration();
        }
      }
    }, 3000);

    setCheckingInterval(interval);

    // Auto stop checking after 10 minutes
    setTimeout(() => {
      if (interval) {
        clearInterval(interval);
        setCheckingInterval(null);
      }
    }, 600000); // 10 minutes
  };

  const completeRegistration = async () => {
    try {
      const tempData = JSON.parse(
        localStorage.getItem("tempRegistrationData") || "{}"
      );
      const user = auth.currentUser;

      if (!user || !tempData.name) {
        throw new Error("Registration data not found");
      }

      // NOW save user data to Firestore (only after email verification)
      await setDoc(doc(db, "clients", user.uid), {
        name: tempData.name,
        email: tempData.email,
        contact: tempData.contact,
        uid: user.uid,
        isVerified: true,
        emailVerified: true,
        createdAt: new Date().toISOString(),
      });

      // Store user information in localStorage
      const userData = {
        uid: user.uid,
        name: tempData.name,
        email: tempData.email,
        contact: tempData.contact,
      };
      localStorage.setItem("user", JSON.stringify(userData));

      // Clean up temporary data
      localStorage.removeItem("tempRegistrationData");

      setVerificationStep("verified");
      toast.success(
        "Email verified successfully! Account created and data saved."
      );

      // Redirect after 2 seconds
      setTimeout(() => {
        navigate("/profile");
      }, 2000);
    } catch (error) {
      console.error("Complete registration error:", error);
      toast.error("Failed to complete registration. Please try again.");
      cancelRegistration();
    }
  };

  const cancelRegistration = async () => {
    try {
      // Clear interval
      if (checkingInterval) {
        clearInterval(checkingInterval);
        setCheckingInterval(null);
      }

      // Delete the unverified user account
      if (tempUser) {
        await deleteUser(tempUser);
      } else if (auth.currentUser && !auth.currentUser.emailVerified) {
        await deleteUser(auth.currentUser);
      }

      // Clear temporary data
      localStorage.removeItem("tempRegistrationData");

      setVerificationStep("form");
      setTempUser(null);
      setName("");
      setEmail("");
      setContact("");
      setPassword("");
      toast.info("Registration cancelled. You can try again.");
    } catch (error) {
      console.error("Cancel registration error:", error);
      // If we can't delete the user, just sign them out
      await signOut(auth);
      localStorage.removeItem("tempRegistrationData");
      setVerificationStep("form");
      setTempUser(null);
      toast.info("Registration cancelled. You can try again.");
    }
  };

  const handleGoogleSignIn = async () => {
    setIsSubmitting(true);
    const provider = new GoogleAuthProvider();
    try {
      const userCredential = await signInWithPopup(auth, provider);
      const user = userCredential.user;

      // Google accounts are already verified, so save data immediately
      await setDoc(doc(db, "clients", user.uid), {
        name: user.displayName || "Google User",
        email: user.email.toLowerCase(),
        contact: "", // Google doesn't provide contact number; leave empty
        uid: user.uid,
        isVerified: user.emailVerified,
        createdAt: new Date().toISOString(),
      });

      // Store user information in localStorage
      const userData = {
        uid: user.uid,
        name: user.displayName || "Google User",
        email: user.email.toLowerCase(),
        contact: "",
      };
      localStorage.setItem("user", JSON.stringify(userData));
      toast.success("Signed in with Google successfully!");
      navigate("/profile");
    } catch (error) {
      toast.error(`Google sign-in failed: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      if (checkingInterval) {
        clearInterval(checkingInterval);
      }
    };
  }, [checkingInterval]);

  // Email Verification Pending Step
  if (verificationStep === "verification") {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-100">
        <div className="max-w-md w-full bg-white p-8 shadow-lg text-center">
          <div className="mb-6">
            <Mail className="h-16 w-16 text-orange-600 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">
              Check Your Email
            </h2>
            <p className="text-gray-600">We've sent a verification link to:</p>
            <p className="font-semibold text-gray-800 mt-1">
              {verificationEmail}
            </p>
          </div>

          <div className="space-y-4">
            <div className="p-4 bg-orange-50 rounded">
              <div className="flex items-center justify-center mb-2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-orange-600"></div>
                <span className="ml-2 text-sm font-medium text-orange-600">
                  Waiting for verification...
                </span>
              </div>
              <p className="text-sm text-gray-700">
                Click the verification link in your email. We'll automatically
                detect when you verify and complete your registration.
              </p>
            </div>

            <button
              onClick={resendVerificationEmail}
              className={`w-full py-2 text-orange-600 font-semibold ${
                isResendCooldown || resendCount >= maxResendAttempts
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:text-orange-700"
              }`}
              disabled={isResendCooldown || resendCount >= maxResendAttempts}
            >
              Resend Verification Email
            </button>
            <button
              onClick={cancelRegistration}
              className="w-full py-2 text-gray-500 hover:text-gray-700"
            >
              Cancel Registration
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Account Created Successfully
  if (verificationStep === "verified") {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-100">
        <div className="max-w-md w-full bg-white p-8 shadow-lg text-center">
          <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">
            Account Created Successfully!
          </h2>
          <p className="text-gray-600 mb-6">
            Your email has been verified and your account data has been saved.
            Redirecting to your profile...
          </p>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  // Original Registration Form (keeping exact same UI)
  return (
    <div className="flex h-screen items-center justify-center bg-gray-100">
      <div className="md:flex w-full max-w-4xl bg-white shadow-lg">
        <div className="md:w-1/2 bg-white p-8 shadow-lg">
          <div className="flex items-center justify-center mb-6">
            <img
              src={require("../assets/images/logo.jpg")}
              alt="Project Portal Logo"
              className="h-20"
            />
          </div>
          <h2 className="text-2xl font-semibold text-gray-800 mb-6">
            Register
          </h2>
          <form onSubmit={handleRegister}>
            <input
              type="text"
              placeholder="Name"
              className="w-full mb-4 p-2 border rounded"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
            <input
              type="email"
              placeholder="Email"
              className="w-full mb-4 p-2 border rounded"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <input
              type="text"
              placeholder="Contact (e.g., +1234567890)"
              className="w-full mb-4 p-2 border rounded"
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              required
            />
            <div className="relative mb-4">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Password (minimum 6 characters)"
                className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
            <button
              type="submit"
              className={`w-full ${
                isSubmitting
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-orange-600"
              } text-white py-2 rounded`}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Sending Verification..." : "Register"}
            </button>
          </form>
          <div className="mt-4">
            <button
              onClick={handleGoogleSignIn}
              className={`w-full flex items-center justify-center gap-2 ${
                isSubmitting
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-white border border-gray-300 hover:bg-gray-50"
              } text-gray-800 py-2 rounded shadow-sm transition-all duration-200`}
              disabled={isSubmitting}
            >
              <img
                src={require("../assets/images/Signup/google-button.png")}
                alt="Custom Icon"
                className="w-5 h-5 mr-3"
              />
              {isSubmitting ? "Processing..." : "Sign up with Google"}
            </button>
          </div>
        </div>
        <div className="md:w-1/2 p-8 bg-gray-50">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">
            Enjoying productivity and insights with Project Portal?
          </h3>
          <p className="mb-4">
            <span className="text-green-600 font-bold">Yes!</span> Contact us
            about adding your other projects to Project Portal.
          </p>
          <p>
            I have feedback.{" "}
            <a href="#" className="text-blue-500 font-semibold hover:underline">
              Share
            </a>{" "}
            your ideas and feedback with us.
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
